import React from 'react';
import PhoneStack from './PhoneStack';

const SITE_URL = 'https://www.riyalbusiness.com';
// the site's own palette: espresso ink, ember accent, paper ground
const INK = '#2B1A12';
const EMBER = '#E2643A';
const PAPER = '#F6F0E6';

/* Captured 2026-10-16 at phone width (new colours), the morning-brief popup closed. The front phone runs the live site. */
const SITE_PAGES = [
  { file: 'home', label: 'Home', path: '/' },
  { file: 'spending', label: 'Card spending', path: '/spending' },
  { file: 'numbers', label: 'Indicators', path: '/numbers' },
  { file: 'inflation', label: 'Inflation', path: '/indicator/inflation' },
  { file: 'cities', label: 'Cities', path: '/cities' },
  { file: 'business', label: 'Business', path: '/business' },
  { file: 'banking', label: 'Banking', path: '/banking' },
  { file: 'wages', label: 'Wages', path: '/wages' },
  { file: 'apps', label: 'App market', path: '/apps' },
  { file: 'tenders', label: 'Tenders', path: '/tenders' },
  { file: 'about', label: 'About', path: '/about' }
];

/* What a reader gets, in the site's own colours. */
const VALUES = [
  { head: 'One place', body: 'Spending, prices, wages, banking, cities, apps and tenders, side by side instead of across dozens of reports.' },
  { head: 'Every morning', body: 'New numbers each day, and a short morning brief by email so you start the day knowing what moved.' },
  { head: 'Plain Arabic', body: 'Each number comes with a sentence that says what it means, written the way Saudis read.' }
];

const MetaItem = ({ label, value }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--text-secondary)', marginBottom: '7px' }}>{label}</div>
    <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text-primary)' }}>{value}</div>
  </div>
);

const Button = ({ href, children, primary }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" style={{
    display: 'inline-flex', alignItems: 'center', padding: '12px 20px', borderRadius: '10px',
    fontSize: '14px', fontWeight: 600, textDecoration: 'none',
    background: primary ? EMBER : 'transparent', color: primary ? '#fff' : 'var(--text-primary)',
    border: primary ? `1px solid ${EMBER}` : '1px solid var(--border-color)'
  }}>{children}</a>
);

const Hero = () => (
  <div>
    <div style={{
      // the brown of the site's morning-brief box, with the light wordmark and a yellow dot
      background: INK, borderRadius: '18px', padding: 'clamp(40px, 8vw, 80px) 24px',
      display: 'grid', placeItems: 'center', marginBottom: 'clamp(28px, 4vw, 40px)'
    }}>
      <img src="/riyalbusiness/logo-on-brown.svg" alt="ريال بزنس — Riyal Business" style={{ width: 'min(70%, 320px)', height: 'auto', display: 'block' }} />
    </div>
    <span style={{ display: 'block', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.18em', color: EMBER, marginBottom: '10px' }}>
      Data &amp; Media
    </span>
    <h1 style={{ margin: 0, fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 700, letterSpacing: '-0.03em', color: 'var(--text-primary)' }}>
      Riyal Business
    </h1>
    <p dir="rtl" lang="ar" style={{ textAlign: 'left', margin: '10px 0 0', fontSize: 'clamp(1.1rem, 2.4vw, 1.35rem)', color: 'var(--text-secondary)' }}>
      بيانات الاقتصاد السعودي
    </p>
    <p style={{ margin: '18px 0 0', maxWidth: '62ch', fontSize: '1.05rem', lineHeight: 1.7, color: 'var(--text-primary)' }}>
      The Saudi economy in one place, in clear Arabic. How much people spend each week, where prices are heading,
      what jobs pay, which cities are growing and which government tenders just opened. Fresh every morning,
      with a short brief in your inbox before work.
    </p>
    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', margin: '26px 0 34px' }}>
      <Button href={SITE_URL} primary>Visit riyalbusiness.com →</Button>
      <Button href={`${SITE_URL}/numbers`}>See the indicators</Button>
    </div>
    <div style={{
      // flex, not grid: responsive.css forces every inline "fr" grid to one column on phones
      display: 'flex', justifyContent: 'space-between', gap: 'clamp(8px, 3vw, 30px)',
      paddingTop: '26px', borderTop: '1px solid var(--border-color)'
    }}>
      <MetaItem label="Category" value="Data & Media" />
      <MetaItem label="Languages" value="Arabic · English" />
      <MetaItem label="Status" value="Live" />
    </div>
  </div>
);

const Values = () => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
    {VALUES.map((v) => (
      <div key={v.head} style={{ flex: '1 1 220px', background: INK, color: PAPER, borderRadius: '14px', padding: '22px 20px' }}>
        <div style={{ fontSize: '1.15rem', fontWeight: 700, color: EMBER }}>{v.head}</div>
        <div style={{ fontSize: '14px', lineHeight: 1.6, marginTop: '8px', opacity: 0.9 }}>{v.body}</div>
      </div>
    ))}
  </div>
);

/* The sections, by what each one tells you. */
const INSIDE = [
  ['Card spending', 'How much Saudis spend each week, and on what.'],
  ['Prices and inflation', 'Whether everyday prices are rising or easing.'],
  ['Wages', 'What jobs pay in Saudi Arabia.'],
  ['Banking', 'Banks and financing, in numbers.'],
  ['Cities', 'How each city is doing, side by side.'],
  ['Business', 'Businesses and enterprises, sector by sector.'],
  ['App market', 'Which apps Saudis download and what they cost.'],
  ['Government tenders', 'New tenders as they open, by ministry and city.']
];

const Inside = () => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
    {INSIDE.map(([t, d]) => (
      <div key={t} style={{ flex: '1 1 240px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', borderRadius: '12px', padding: '16px 18px' }}>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{t}</div>
        <div style={{ fontSize: '14px', marginTop: '4px', color: 'var(--text-secondary)' }}>{d}</div>
      </div>
    ))}
  </div>
);

const SUN = '#F2C84B'; // the site's own yellow (its subscribe button)

/* The last card, in the brown of the site's morning-brief box, like Pawtimo's pink one. */
const BrownEnd = () => (
  <div style={{
    borderRadius: '28px', padding: 'clamp(44px, 8vw, 72px) 24px', textAlign: 'center',
    background: INK, color: PAPER, boxShadow: '0 22px 50px rgba(43,26,18,0.35)'
  }}>
    <img src="/riyalbusiness/logo-on-brown.svg" alt="ريال بزنس — Riyal Business" style={{ width: 'min(64%, 260px)', height: 'auto' }} />
    <div dir="rtl" lang="ar" style={{ fontSize: 'clamp(1.05rem, 2.6vw, 1.3rem)', marginTop: '18px', opacity: 0.9 }}>بيانات الاقتصاد السعودي</div>
    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '26px' }}>
      <a href={SITE_URL} target="_blank" rel="noopener noreferrer" style={{
        padding: '12px 20px', borderRadius: '14px', background: SUN, color: INK, fontWeight: 700, fontSize: '14px', textDecoration: 'none'
      }}>Visit riyalbusiness.com</a>
      <a href="/" style={{
        padding: '12px 20px', borderRadius: '14px', background: 'rgba(246,240,230,0.08)', color: PAPER, fontWeight: 600,
        fontSize: '14px', textDecoration: 'none', border: '1px solid rgba(246,240,230,0.35)'
      }}>See more work ↗</a>
    </div>
  </div>
);

const Text = ({ children }) => (
  <p style={{ margin: '0 0 16px', maxWidth: '64ch', fontSize: '1rem', lineHeight: 1.75, color: 'var(--text-primary)' }}>{children}</p>
);

export const riyalbusinessContent = {
  pageMaxWidth: '1100px',
  breadcrumb: { text: 'Back to Home', url: '/' },
  sections: [
    { id: 'hero', spacing: 'clamp(48px, 7vw, 80px)', blocks: [{ type: 'custom', render: () => <Hero /> }] },
    {
      id: 'values',
      spacing: 'clamp(48px, 7vw, 80px)',
      blocks: [{ type: 'custom', render: () => <Values /> }]
    },
    {
      id: 'site',
      title: 'The site',
      blocks: [{ type: 'custom', render: () => <PhoneStack pages={SITE_PAGES} baseUrl={SITE_URL} imgBase="/riyalbusiness/" /> }]
    },
    {
      id: 'inside',
      title: "What's inside",
      blocks: [{ type: 'custom', render: () => <Inside /> }]
    },
    {
      id: 'who',
      title: 'Who it is for',
      blocks: [{
        type: 'custom',
        render: () => (
          <Text>Business owners deciding where to open next, investors following the market, journalists who need a number they can cite,
          and anyone who wants to understand the Saudi economy without reading a stack of reports.</Text>
        )
      }]
    },
    { id: 'end', blocks: [{ type: 'custom', render: () => <BrownEnd /> }] }
  ]
};
